import type { PitstopApplicationContext } from "core/components/PitstopApp";
import type { PublicApplicationContext } from "core/components/PublicApp";
import { determineApplicationId } from "core/utils/determineApplicationId";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import {
  getFullClientRuntimeConfig,
  getPublicClientRuntimeConfig,
} from "core/utils/getClientRuntimeConfig";
import { getPitstopConfig } from "core/utils/getPitstopConfig";
import { trackServerException } from "core/utils/trackServerException";
import App, { type AppContext, type AppInitialProps } from "next/app";

type ClientAppProps = {
  executionContext: "client";
};
type ServerErrorAppProps = {
  executionContext: "server";
  ok: false;
  error: { message: string };
  requestInfo: {
    origin: string;
    host: string;
  };
};
type ServerSuccessAppProps = {
  executionContext: "server";
  ok: true;
  applicationContext: PitstopApplicationContext | PublicApplicationContext;
};

type ServerAppProps = ServerErrorAppProps | ServerSuccessAppProps;

export type ApplicationMainProps = ClientAppProps | ServerAppProps;

async function getAppMainProps(
  host: string | undefined,
  protocol: string = "http",
  pathname: string
): Promise<ApplicationMainProps> {
  if (!host) {
    // If there is no host, we are likely on the client side or in a context where the host is unavailable.
    return {
      executionContext: "client",
    };
  }
  const origin = `${protocol}://${host}`;

  const configResult = await getPitstopConfig(host);
  if (!configResult.ok) {
    trackServerException({
      exception: new Error(`
        Failed to get Pitstop config for host: "${host}"
        Error: ${configResult.error?.message ?? "Unknown error"}
      `),
      properties: { host: host },
      severity: "Critical",
    });
    return {
      executionContext: "server",
      requestInfo: {
        origin: origin,
        host: host,
      },
      ok: false,
      error: configResult.error,
    };
  }
  const applicationId = determineApplicationId(pathname);
  switch (applicationId) {
    case "pitstop": {
      const envConfig = getFullClientRuntimeConfig();
      return {
        executionContext: "server",
        ok: true,
        applicationContext: {
          applicationId: "pitstop",
          brandTwoLetterCode: configResult.brandTwoLetterCode,
          availableRegions: configResult.availableRegions,
          environmentVariables: envConfig,
          requestInfo: {
            origin: origin,
            host: host,
          },
        },
      };
    }
    case "public": {
      const envConfig = getPublicClientRuntimeConfig();
      return {
        executionContext: "server",
        ok: true,
        applicationContext: {
          applicationId: "public",
          brandTwoLetterCode: configResult.brandTwoLetterCode,
          availableRegions: configResult.availableRegions,
          environmentVariables: envConfig,
          requestInfo: {
            origin: origin,
            host: host,
          },
        },
      };
    }
    default: {
      exhaustiveCheck(applicationId);
      return {
        executionContext: "server",
        ok: false,
        requestInfo: {
          origin: origin,
          host: host,
        },
        error: { message: `Unhandled applicationId: "${applicationId}"` },
      };
    }
  }
}

export async function getApplicationInitialProps(
  context: AppContext
): Promise<AppInitialProps & ApplicationMainProps> {
  // https://nextjs.org/docs/advanced-features/custom-app
  const appProps = await App.getInitialProps(context);
  const protocol =
    typeof context.ctx.req?.headers["x-forwarded-proto"] === "string"
      ? context.ctx.req?.headers["x-forwarded-proto"]
      : undefined;
  const appMainProps = await getAppMainProps(
    context.ctx.req?.headers?.host,
    protocol,
    context.ctx.pathname
  );
  if (
    appMainProps.executionContext === "server" &&
    typeof context.ctx.res !== "undefined" &&
    appMainProps.ok === false
  ) {
    context.ctx.res.statusCode = 500;
  }
  return { ...appProps, ...appMainProps };
}
